import { z } from 'zod';

import { UuidSchema } from '../common/id/id';
import { AnswerValueSchema } from '../questions/answer-value-schema/answer-value-schema';
import { QuestionIdentityRoleSchema } from '../questions/question-identity-role/question-identity-role';
import { QuestionTypeSchema } from '../questions/question-type/question-type';
import { StableKeySchema } from '../questions/stable-key/stable-key';
import { ViewerContextSchema } from '../visibility/viewer-context/viewer-context';

/**
 * One answer as **another member** is allowed to see it — the output of
 * `ProfileVisibilityResolver` (N18) and the only shape member-to-member answer
 * data ever takes.
 *
 * It carries the rendered `label` and, for option-backed types, the chosen
 * options' labels: a viewer must never be handed a raw slug to render, and must
 * never need a second call to turn `never_married` into "لم يسبق له الزواج".
 */
export const VisibleAnswerSchema = z
  .object({
    questionId: UuidSchema,
    questionKey: StableKeySchema,
    type: QuestionTypeSchema,
    label: z.string(),
    value: AnswerValueSchema,
    /** Locale-resolved labels for the chosen option values; empty for other types. */
    valueLabels: z.array(z.string()),
    /**
     * The question's `identityRole`, so a client can lead with the answer staff
     * named as the headline rather than with whichever sorts first. Travels on
     * the answer because a hidden headline is ABSENT here (N18), and the client
     * must fall through to the next one without knowing what it never received.
     */
    identityRole: QuestionIdentityRoleSchema.nullable(),
    sortOrder: z.number().int().min(0),
  })
  .strict();

export type VisibleAnswer = z.infer<typeof VisibleAnswerSchema>;

/**
 * Everything one viewer may see of one member's answers.
 *
 * **Hidden answers are absent from `answers`, not present with a null value** —
 * an acceptance criterion of this phase, and a real distinction: a `null` entry
 * tells the viewer the question exists and that this member declined it, which
 * for a `forced_hidden` field is precisely the fact the policy exists to
 * withhold. The resolver filters; it does not blank.
 *
 * `viewerContext` is echoed so a client can tell a public payload from a matched
 * one without inferring it — the two are the same shape, and rendering one as
 * the other is how a browse card would claim to be a match.
 */
export const VisibleAnswersSchema = z
  .object({
    ownerUserId: UuidSchema,
    viewerContext: ViewerContextSchema,
    answers: z.array(VisibleAnswerSchema),
  })
  .strict();

export type VisibleAnswers = z.infer<typeof VisibleAnswersSchema>;
